import { json, MetaFunction } from '@remix-run/node';
import { useLoaderData, useOutletContext } from '@remix-run/react';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// @ts-ignore - 타입 선언 충돌 해결
declare global {
  interface Window {
    kakao: any;
    ENV: {
      KAKAO_MAP_API_KEY: string;
    };
  }
}

export const meta: MetaFunction = () => {
  return [
    { title: '오시는 길 - 법무법인 휘상' },
    { name: 'description', content: '법무법인 휘상의 위치와 오시는 길을 안내해드립니다.' }
  ];
};

export const loader = async () => {
  return json({
    address: {
      main: '서울 강남구 영동대로 611',
      detail: '13층 (찬앤찬타워)',
      postalCode: '06087'
    },
    contact: {
      phone: '02-6959-2170',
      email: 'hwisang@hwisang.co.kr',
      fax: '02-6959-5931'
    },
    transportation: [
      {
        type: '지하철',
        items: [
          '9호선 봉은사역 2번 출구 도보 2분',
          '2호선 삼성역 6번 출구에서 도보 10분'
        ]
      },
      {
        type: '버스',
        items: [
          '봉은사역 정류장: 301, 362, 401, 2415, 3411, 9407, 9507, 9607',
          '삼성역 정류장: 143, 146, 333, 341, 401, 2415, 3411, 4318, 9407, 9414, 9607'
        ]
      },
      {
        type: '주차',
        items: [
          '주차 가능합니다.(기계식 주차)'
        ]
      }
    ]
  });
};

export default function LocationPage() {
  const { address, contact, transportation } = useLoaderData<typeof loader>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // 카카오맵 로드 및 초기화
  useEffect(() => {
    const loadKakaoMap = () => {
      if (typeof window.kakao === 'undefined') {
        const script = document.createElement('script');
        script.async = true;
        
        // 환경 변수에서 API 키 가져오기
        // @ts-ignore - 타입 체크 무시
        const apiKey = window.ENV?.KAKAO_MAP_API_KEY || '';
        
        if (!apiKey) {
          console.error('카카오맵 API 키가 설정되지 않았습니다');
          return;
        }
        
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false`;
        
        script.onload = () => initializeMap();
        document.head.appendChild(script);
      } else {
        initializeMap();
      }
    };

    const initializeMap = () => {
      if (!mapContainer.current) return;
      
      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => {
          const options = {
            center: new window.kakao.maps.LatLng(37.5147252914267, 127.05940565552), // 영동대로 611 찬앤찬타워 정확한 좌표
            level: 3
          };
          
          const map = new window.kakao.maps.Map(mapContainer.current, options);
          
          // 마커 추가
          const marker = new window.kakao.maps.Marker({
            position: map.getCenter()
          });
          marker.setMap(map);
          
          // 인포윈도우 추가
          const infowindow = new window.kakao.maps.InfoWindow({
            content: '<div class="p-2 text-center font-medium text-gray-800">법무법인 휘상</div>'
          });
          infowindow.open(map, marker);
          
          // 지도 컨트롤 추가
          const zoomControl = new window.kakao.maps.ZoomControl();
          map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);
          
          setIsMapLoaded(true);
        });
      }
    };

    // 페이지 로드 시 한 번만 실행
    const timer = setTimeout(loadKakaoMap, 1000); // 약간의 지연을 줘서 초기화 문제 방지
    
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // 주소 복사 기능
  const copyAddress = () => {
    navigator.clipboard.writeText(address.main + ' ' + address.detail).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  // 애니메이션 변수
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const mapVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        delay: 0.3
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        when: "beforeChildren", 
        staggerChildren: 0.1 
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
        delay: 0.2
      }
    }
  };

  return (
    <div className="pt-16 min-h-screen">
      <div className="relative bg-white py-16 overflow-hidden border-b">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={headerVariants}
            className="text-center md:text-left"
          >
            <motion.h1 
              variants={titleVariants}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight"
            >
              오시는 길
            </motion.h1>
            <motion.p 
              variants={subtitleVariants}
              className="text-lg text-gray-600 max-w-3xl font-light"
            >
              법무법인 휘상을 찾아오시는 방법을 안내해드립니다
            </motion.p>
          </motion.div>
        </div>
      </div>
      
      <div className="bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            className="py-12"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div
                variants={mapVariants}
                className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden h-full"
              >
                <div className="w-full h-full min-h-[400px] lg:min-h-[600px]">
                  <div ref={mapContainer} className="w-full h-full relative">
                    {!isMapLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b98e6a] mb-2"></div>
                          <p className="text-[#b98e6a]">지도를 불러오는 중...</p>
                          <p className="text-gray-500 text-sm mt-1">잠시만 기다려주세요</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              <motion.div variants={containerVariants} className="flex flex-col space-y-6">
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-md">
                  <div className="flex items-start">
                    <div className="bg-[#b98e6a]/10 p-3 rounded-full mr-4">
                      <svg className="w-6 h-6 text-[#b98e6a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold mb-2 text-gray-900">주소</h2>
                      <p className="text-gray-700 mb-1">{address.main}</p>
                      <p className="text-gray-700 mb-2">{address.detail}</p>
                      <p className="text-gray-500 text-sm">우편번호: {address.postalCode}</p>
                      
                      <button 
                        onClick={copyAddress}
                        className="mt-3 inline-flex items-center px-3 py-1.5 bg-[#b98e6a]/10 text-[#b98e6a] text-sm rounded-lg hover:bg-[#b98e6a]/20 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {isCopied ? '복사됨' : '주소 복사'}
                      </button>
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-md">
                  <div className="flex items-start">
                    <div className="bg-[#b98e6a]/10 p-3 rounded-full mr-4">
                      <svg className="w-6 h-6 text-[#b98e6a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold mb-2 text-gray-900">연락처</h2>
                      <div className="mb-1.5">
                        <span className="text-gray-500">전화:</span>
                        <a href={`tel:${contact.phone}`} className="text-gray-700 ml-2 hover:text-[#b98e6a] transition-colors">{contact.phone}</a>
                      </div>
                      <div className="mb-1.5">
                        <span className="text-gray-500">팩스:</span>
                        <span className="text-gray-700 ml-2">{contact.fax}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">이메일:</span>
                        <a href={`mailto:${contact.email}`} className="text-gray-700 ml-2 hover:text-[#b98e6a] transition-colors">{contact.email}</a>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-r from-amber-700 to-amber-900 text-white py-3 px-5">
                    <h3 className="text-xl font-bold mb-1">교통 안내</h3>
                    <p className="text-amber-100 text-sm">다양한 교통편으로 찾아오실 수 있습니다</p>
                  </div>
                  <div className="p-5">
                    {transportation.map((transport, idx) => (
                      <div 
                        key={idx} 
                        className={`mb-4 ${idx !== transportation.length - 1 ? 'border-b border-gray-200 pb-4' : ''}`}
                      >
                        <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          {transport.type === '지하철' && (
                            <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 16V7a4 4 0 0 1 8 0v9M6 21h12a2 2 0 0 0 2-2v-6.26A2 2 0 0 0 19 11h-1V7a5 5 0 0 0-10 0v4H7a2 2 0 0 0-1 3.74V19a2 2 0 0 0 2 2z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M12 16a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill="currentColor" />
                            </svg>
                          )}
                          {transport.type === '버스' && (
                            <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 11V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4 14v3a2 2 0 0 0 2 2h1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M17 19h1a2 2 0 0 0 2-2v-3" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M6 19L6 21" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18 19L18 21" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M8 10L8 13" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M16 10L16 13" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4 11L20 11" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4 14L20 14" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {transport.type === '주차' && (
                            <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 9h4a2 2 0 1 0 0-4h-4v8" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M3 12a9 9 0 1 0 18 0 9 9 0 1 0-18 0z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {transport.type}
                        </h4>
                        <ul className="space-y-2">
                          {transport.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start ml-7">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600 mt-2 mr-2"></span>
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            <motion.div 
              variants={itemVariants} 
              className="mt-12 bg-white p-6 md:p-8 rounded-2xl shadow-md"
            >
              <div className="flex items-start mb-2">
                <div className="bg-[#b98e6a]/10 p-3 rounded-full mr-4">
                  <svg className="w-6 h-6 text-[#b98e6a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">영업 시간</h2>
              </div>
              <div className="md:flex justify-start items-center space-y-2 md:space-y-0 md:space-x-8 ml-12">
                <p className="text-gray-700">
                  <span className="font-medium">평일:</span> 오전 9:00 - 오후 6:00
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">점심시간:</span> 오후 12:00 - 오후 1:00
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">주말 및 공휴일:</span> 휴무
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
